// UX_REFINEMENTS_v2 §3 — the canonical snake-draft board.
//
// Owners as column headers across the top. Picks fill cells in snake (or
// linear) order. Each cell is tinted by the player's NFL team color, with
// a small trade indicator ("→ Owner") when the pick was traded.
//
// Renders for draftType 'snake' or 'linear'. Auction stays in list view
// (no meaningful round structure). Mobile gets horizontal scroll with a
// right-edge fade hint instead of falling back to a list — the grid is
// the recognizable artifact and worth preserving even when squeezed.

import { useMemo } from "react";
import theme from "../../data/theme";
import { POS_COLORS } from "../../data/players";

// A1 Patch 6 — draft grid color scheme switched from NFL team colors
// (src/data/teamColors.js) to fantasy position colors (POS_COLORS from
// src/data/players.js). QB=red, RB=green, WR=blue, TE=orange, FLEX=purple,
// SFLEX=pink, K=brown, DEF=cyan, BN=slate. Cell text retains the team
// abbreviation so the NFL team is still visible; just no longer
// determines the cell's background color. teamColors.js stays in the
// codebase for share-card or other future surfaces.
const DEFAULT_POS_COLOR = POS_COLORS.BN;

const MIN_CELL_WIDTH = 116;

export default function DraftBoardGrid({ draft }) {
  const { picks, rounds, draftType } = draft;

  const { columnOwners, grid, teamCount } = useMemo(() => {
    // Determine column count from the highest slot in the data. Sleeper's
    // `pick` field (= draft_slot) is the in-round slot 1..N — constant per
    // owner across rounds, regardless of snake/linear ordering.
    let N = 0;
    for (const p of picks || []) {
      if (p.pick > N) N = p.pick;
    }
    // Column-owner header: prefer originalOwner (the slot's true owner from
    // draft_order) — falls back to the round-1 actual owner if draft_order
    // wasn't populated. Round 1 actuals = slot owners in either snake or
    // linear, since round 1 picks always go in slot order.
    const colOwners = Array(N).fill(null);
    for (const p of picks || []) {
      const col = p.pick - 1;
      if (col < 0 || col >= N) continue;
      if (!colOwners[col]) {
        colOwners[col] = p.originalOwner || (p.round === 1 ? p.owner : null);
      } else if (p.round === 1 && p.originalOwner) {
        // Prefer originalOwner from round 1 over a later-round fallback.
        colOwners[col] = p.originalOwner;
      }
    }
    // 2D grid: rows = rounds, columns = N owners. Column derivation is
    // uniform — col = slot - 1 — because Sleeper's draft_slot already
    // encodes "owner X is at column Y" constantly across rounds. The snake
    // visual effect (round 2 reads right-to-left) is purely about pick
    // ORDER within the round, not about which column the pick lives in.
    const g = Array.from({ length: rounds || 0 }, () => Array(N).fill(null));
    for (const p of picks || []) {
      const col = p.pick - 1;
      if (p.round >= 1 && p.round <= (rounds || 0) && col >= 0 && col < N) {
        g[p.round - 1][col] = p;
      }
    }
    return { columnOwners: colOwners, grid: g, teamCount: N };
  }, [picks, rounds, draftType]);

  if (!teamCount || !rounds) return null;

  const gridTemplate = `repeat(${teamCount}, minmax(${MIN_CELL_WIDTH}px, 1fr))`;

  return (
    <div className="draft-board-wrap" style={{ position: "relative" }}>
      <div className="draft-board-scroll" style={{ overflowX: "auto" }}>
        <div
          role="grid"
          aria-label="Draft board"
          style={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            gap: 4,
            minWidth: `${teamCount * MIN_CELL_WIDTH}px`,
          }}
        >
          {/* Header row — column owners. */}
          {columnOwners.map((owner, i) => (
            <HeaderCell key={`hdr-${i}`} owner={owner} slot={i + 1} />
          ))}
          {/* Pick rows — round 1 top to round N bottom, snake/linear ordered. */}
          {grid.flatMap((row, ri) =>
            row.map((pick, ci) => (
              <PickCell key={`r${ri}-c${ci}`} pick={pick} round={ri + 1} teamCount={teamCount} />
            ))
          )}
        </div>
      </div>
      {/* Right-edge fade hint that mobile scroll exists. Pointer-events
          disabled so it doesn't intercept taps on cells underneath. */}
      <div
        className="draft-board-fade"
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0, right: 0, bottom: 0,
          width: 28, pointerEvents: "none",
          background: `linear-gradient(to right, transparent, ${theme.colors.bg.app})`,
        }}
      />
    </div>
  );
}

function HeaderCell({ owner, slot }) {
  return (
    <div
      role="columnheader"
      style={{
        background: theme.colors.bg.surface,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: 6,
        padding: "6px 8px",
        textAlign: "center",
        fontFamily: theme.fonts.display,
      }}
    >
      <div style={{
        fontSize: 9, color: theme.colors.text.muted, letterSpacing: 1.5,
        textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
      }}>Slot {slot}</div>
      <div style={{
        fontSize: 12, color: theme.colors.text.primary,
        fontWeight: theme.fontWeight.heavy, marginTop: 2,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{owner || "—"}</div>
    </div>
  );
}

function PickCell({ pick, round, teamCount }) {
  if (!pick) {
    return (
      <div
        role="cell"
        style={{
          background: theme.colors.bg.deep,
          border: `1px dashed ${theme.colors.border.default}`,
          borderRadius: 6, minHeight: 64,
        }}
      />
    );
  }
  // Position-color scheme (A1 Patch 6). Cell bg/text come from the
  // player's position; team abbreviation is shown in the cell body.
  // POS_COLORS uses { bg, text, light, mid, border } — we use bg + text.
  const posKey = pick.position && POS_COLORS[pick.position] ? pick.position : null;
  const color = posKey
    ? { bg: POS_COLORS[posKey].bg, fg: POS_COLORS[posKey].text }
    : { bg: DEFAULT_POS_COLOR.bg, fg: DEFAULT_POS_COLOR.text };
  const isSteal = pick.isSteal;
  const isBust = pick.isBust;
  const finishRank = pick.seasonStats?.positionRank;
  return (
    <div
      role="cell"
      style={{
        background: color.bg,
        color: color.fg,
        border: `1px solid rgba(0,0,0,0.25)`,
        borderRadius: 6,
        padding: "6px 8px",
        fontFamily: theme.fonts.body,
        minHeight: 64,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
        // #22-keeper — amber inset ring so keeper cells pop across the grid.
        boxShadow: pick.isKeeper ? "inset 0 0 0 2px #f59e0b" : undefined,
      }}
    >
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "baseline", gap: 6,
      }}>
        <span style={{
          fontSize: 12, fontWeight: 700, lineHeight: 1.2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{shortenName(pick.player)}</span>
        <span style={{
          fontSize: 9, opacity: 0.75, fontFamily: theme.fonts.display,
          fontWeight: 700, letterSpacing: 0.5, flexShrink: 0,
        }}>{round}.{String(inRoundPickFromOverall(pick, teamCount)).padStart(2, "0")}</span>
      </div>
      <div style={{
        fontSize: 10, opacity: 0.82,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {pick.position || "—"} · {pick.team || "—"}
        {finishRank != null && ` · ${pick.position}${finishRank}`}
      </div>
      {pick.isKeeper && (
        <span
          title="Keeper pick"
          style={{
            position: "absolute", top: 3, right: 3,
            fontSize: 9, lineHeight: 1, padding: "2px 5px", borderRadius: 4,
            background: "#f59e0b", color: "#1a1205",
            fontWeight: 900, letterSpacing: 0.6,
            border: "1px solid rgba(0,0,0,0.3)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
          }}
        >KEEP</span>
      )}
      {(isSteal || isBust) && !pick.isKeeper && (
        <span
          title={isSteal
            ? `Steal — finished ${pick.position}${finishRank}, expected ${pick.expectedPositionRank} (+${pick.valueOverExpected})`
            : `Bust — finished ${pick.position}${finishRank}, expected ${pick.expectedPositionRank} (${pick.valueOverExpected})`}
          style={{
            position: "absolute", top: 4, right: 4,
            fontSize: 10, lineHeight: 1,
          }}
        >{isSteal ? "★" : "✗"}</span>
      )}
      {pick.wasTraded && (
        <div style={{
          fontSize: 9, opacity: 0.88, marginTop: 2,
          fontStyle: "italic",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          → {pick.owner}
        </div>
      )}
    </div>
  );
}

// "Justin Jefferson" → "J. Jefferson". "Ja'Marr Chase" → "J. Chase".
// Single-name (rare — usually a defense) returned as-is.
function shortenName(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return name;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
}

// In-round pick number from overall pick (for the "1.01" / "2.05" labels
// in cells). Snake even rounds: pick_no 11 = slot 10 = first pick of
// round 2 = displayed as "2.01" (NOT "2.10"). Computed from overallPick
// so it's correct for snake, linear, and any future Sleeper draft type.
// Falls back to slot when overallPick is missing (auctions, partial data).
function inRoundPickFromOverall(pick, teamCount) {
  if (!teamCount || !pick.overallPick) return pick.pick;
  return ((pick.overallPick - 1) % teamCount) + 1;
}
